// Reading-room shell: slim translucent top bar over the paper wash. Gradient
// wordmark on light surface (positive logo rule), nav for publisher admins,
// language switch + user chip with gradient avatar ring.
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { LangSwitcher } from './LangSwitcher';

export function AppShell({ children }: { children: ReactNode }) {
  const { user, canPublish, isPlatformAdmin, logout } = useAuth();
  const { t } = useTranslation('common');

  const initials = (user?.displayName ?? '?')
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const navItem = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? 'text-brand-rose' : 'text-slate-500 hover:text-ink'
    } after:absolute after:left-3 after:right-3 after:-bottom-[13px] after:h-[2px] after:rounded-full after:bg-brand-gradient after:transition-opacity ${
      isActive ? 'after:opacity-100' : 'after:opacity-0'
    }`;

  return (
    <div className="min-h-dvh paper-wash">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200/70">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center gap-6">
          <NavLink to="/" className="flex items-baseline gap-2 shrink-0">
            <span className="text-xl font-bold gradient-text tracking-tight">UniPortal</span>
            <span className="hidden sm:inline text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              {t('byUnifiber')}
            </span>
          </NavLink>

          <nav className="flex items-center gap-1 border-b-0">
            <NavLink to="/" end className={navItem}>
              {t('nav.documents')}
            </NavLink>
            {canPublish && (
              <>
                <NavLink to="/publish" className={navItem}>
                  {t('nav.publishing')}
                </NavLink>
                <NavLink to="/collections" className={navItem}>
                  {t('nav.collections')}
                </NavLink>
                <NavLink to="/channels" className={navItem}>
                  {t('nav.channels')}
                </NavLink>
                <NavLink to="/admin/users" className={navItem}>
                  {t('nav.users')}
                </NavLink>
              </>
            )}
            {isPlatformAdmin && (
              <NavLink to="/admin/publishers" className={navItem}>
                {t('nav.publishers')}
              </NavLink>
            )}
            <NavLink to="/guide" className={navItem}>
              {t('nav.guide')}
            </NavLink>
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <LangSwitcher variant="paper" />
            <div className="hidden md:flex items-center gap-2.5 pl-3 border-l border-slate-200">
              <div className="p-[2px] rounded-full bg-brand-gradient">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[11px] font-bold text-brand-purple">
                  {initials}
                </div>
              </div>
              <div className="leading-tight">
                <p className="text-xs font-semibold text-ink max-w-[14rem] truncate">{user?.displayName}</p>
                <p className="text-[10px] text-slate-400 max-w-[14rem] truncate">{user?.email}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              title={t('signOut')}
              className="p-2 rounded-lg text-slate-400 hover:text-brand-rose hover:bg-brand-rose/5 transition-colors"
            >
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-10">{children}</main>
    </div>
  );
}
